import { Mat4 } from '../math/mat4';
import { Vec3 } from '../math/vec3';
import { Quat } from '../math/quat';
import { AABB } from '../math/aabb';
import { fbm2 } from '../math/noise';

export type Color = readonly [number, number, number];

/** CPU-side indexed triangle mesh. All arrays are flat; per-vertex color + emissive. */
export interface MeshData {
  positions: number[];
  normals: number[];
  colors: number[];
  emissive: number[];
  indices: number[];
}

export function emptyMesh(): MeshData {
  return { positions: [], normals: [], colors: [], emissive: [], indices: [] };
}
export function vertexCount(m: MeshData): number { return m.positions.length / 3; }
export function triangleCount(m: MeshData): number { return m.indices.length / 3; }

/** Append b into a (mutates a). */
export function merge(a: MeshData, ...rest: MeshData[]): MeshData {
  for (const b of rest) {
    const base = vertexCount(a);
    for (let i = 0; i < b.positions.length; i++) a.positions.push(b.positions[i]);
    for (let i = 0; i < b.normals.length; i++) a.normals.push(b.normals[i]);
    for (let i = 0; i < b.colors.length; i++) a.colors.push(b.colors[i]);
    for (let i = 0; i < b.emissive.length; i++) a.emissive.push(b.emissive[i]);
    for (let i = 0; i < b.indices.length; i++) a.indices.push(b.indices[i] + base);
  }
  return a;
}

export function clone(m: MeshData): MeshData {
  return { positions: m.positions.slice(), normals: m.normals.slice(), colors: m.colors.slice(), emissive: m.emissive.slice(), indices: m.indices.slice() };
}

/** Transform positions and normals by a matrix (mutates). */
export function transform(m: MeshData, mat: Mat4): MeshData {
  const p = new Vec3(), n = new Vec3();
  const normalMat = mat.clone().invert().transpose();
  for (let i = 0; i < m.positions.length; i += 3) {
    p.set(m.positions[i], m.positions[i + 1], m.positions[i + 2]);
    mat.transformPoint(p);
    m.positions[i] = p.x; m.positions[i + 1] = p.y; m.positions[i + 2] = p.z;
    n.set(m.normals[i], m.normals[i + 1], m.normals[i + 2]);
    normalMat.transformDirection(n).normalize();
    m.normals[i] = n.x; m.normals[i + 1] = n.y; m.normals[i + 2] = n.z;
  }
  return m;
}
const _m = new Mat4(), _q = new Quat(), _v = new Vec3(), _s = new Vec3();
export function translate(m: MeshData, x: number, y: number, z: number): MeshData {
  for (let i = 0; i < m.positions.length; i += 3) { m.positions[i] += x; m.positions[i + 1] += y; m.positions[i + 2] += z; }
  return m;
}
export function scale(m: MeshData, x: number, y = x, z = x): MeshData {
  return transform(m, _m.setScale(_s.set(x, y, z)));
}
export function rotate(m: MeshData, axis: Vec3, angle: number): MeshData {
  return transform(m, _m.compose(_v.set(0, 0, 0), _q.setAxisAngle(axis, angle), _s.set(1, 1, 1)));
}
export function rotateX(m: MeshData, a: number): MeshData { return rotate(m, new Vec3(1, 0, 0), a); }
export function rotateY(m: MeshData, a: number): MeshData { return rotate(m, new Vec3(0, 1, 0), a); }
export function rotateZ(m: MeshData, a: number): MeshData { return rotate(m, new Vec3(0, 0, 1), a); }
/** Compose translate/rotate(yaw,pitch,roll)/scale in one go. */
export function place(m: MeshData, pos: Vec3, rot?: Quat, scl?: Vec3): MeshData {
  return transform(m, _m.compose(pos, rot ?? _q.identity(), scl ?? _s.set(1, 1, 1)));
}

/** Unweld every triangle and give it a face normal. Result has 3 unique verts per triangle. */
export function flatShade(m: MeshData): MeshData {
  const out = emptyMesh();
  const a = new Vec3(), b = new Vec3(), c = new Vec3(), n = new Vec3();
  for (let t = 0; t < m.indices.length; t += 3) {
    const i0 = m.indices[t], i1 = m.indices[t + 1], i2 = m.indices[t + 2];
    a.fromArray(m.positions, i0 * 3); b.fromArray(m.positions, i1 * 3); c.fromArray(m.positions, i2 * 3);
    n.copy(b).sub(a).cross(_v.copy(c).sub(a)).normalize();
    const base = vertexCount(out);
    for (const i of [i0, i1, i2]) {
      out.positions.push(m.positions[i * 3], m.positions[i * 3 + 1], m.positions[i * 3 + 2]);
      out.normals.push(n.x, n.y, n.z);
      out.colors.push(m.colors[i * 3], m.colors[i * 3 + 1], m.colors[i * 3 + 2]);
      out.emissive.push(m.emissive[i * 3], m.emissive[i * 3 + 1], m.emissive[i * 3 + 2]);
    }
    out.indices.push(base, base + 1, base + 2);
  }
  return out;
}

/** Recompute smooth (area-weighted) vertex normals in place. */
export function computeNormals(m: MeshData): MeshData {
  const n = new Float32Array(m.positions.length);
  const a = new Vec3(), b = new Vec3(), c = new Vec3(), fn = new Vec3();
  for (let t = 0; t < m.indices.length; t += 3) {
    const i0 = m.indices[t], i1 = m.indices[t + 1], i2 = m.indices[t + 2];
    a.fromArray(m.positions, i0 * 3); b.fromArray(m.positions, i1 * 3); c.fromArray(m.positions, i2 * 3);
    fn.copy(b).sub(a).cross(_v.copy(c).sub(a));
    for (const i of [i0, i1, i2]) { n[i * 3] += fn.x; n[i * 3 + 1] += fn.y; n[i * 3 + 2] += fn.z; }
  }
  for (let i = 0; i < n.length; i += 3) {
    fn.set(n[i], n[i + 1], n[i + 2]).normalize();
    m.normals[i] = fn.x; m.normals[i + 1] = fn.y; m.normals[i + 2] = fn.z;
  }
  return m;
}

/** Set every vertex color via a callback of (position, normal). */
export function colorize(m: MeshData, fn: (p: Vec3, n: Vec3) => Color): MeshData {
  const p = new Vec3(), n = new Vec3();
  for (let i = 0; i < m.positions.length; i += 3) {
    p.fromArray(m.positions, i); n.fromArray(m.normals, i);
    const c = fn(p, n);
    m.colors[i] = c[0]; m.colors[i + 1] = c[1]; m.colors[i + 2] = c[2];
  }
  return m;
}
export function setColor(m: MeshData, c: Color): MeshData {
  for (let i = 0; i < m.colors.length; i += 3) { m.colors[i] = c[0]; m.colors[i + 1] = c[1]; m.colors[i + 2] = c[2]; }
  return m;
}
export function setEmissive(m: MeshData, c: Color, intensity = 1): MeshData {
  for (let i = 0; i < m.emissive.length; i += 3) { m.emissive[i] = c[0] * intensity; m.emissive[i + 1] = c[1] * intensity; m.emissive[i + 2] = c[2] * intensity; }
  return m;
}

/** Displace vertices along their normals by fractal noise. Call before flatShade for a rocky look. */
export function noiseDisplace(m: MeshData, amplitude: number, frequency: number, seed = 0): MeshData {
  for (let i = 0; i < m.positions.length; i += 3) {
    const x = m.positions[i], y = m.positions[i + 1], z = m.positions[i + 2];
    const d = fbm2(x * frequency + y * 0.37, z * frequency - y * 0.61, 2, seed) * amplitude;
    m.positions[i] += m.normals[i] * d; m.positions[i + 1] += m.normals[i + 1] * d; m.positions[i + 2] += m.normals[i + 2] * d;
  }
  return m;
}

export function bounds(m: MeshData): AABB {
  const b = new AABB();
  const p = new Vec3();
  for (let i = 0; i < m.positions.length; i += 3) b.expandByPoint(p.fromArray(m.positions, i));
  return b;
}

/** Sanity check used by tests: finite values, matching array sizes, indices in range. */
export function validate(m: MeshData): string | null {
  const vc = vertexCount(m);
  if (m.normals.length !== vc * 3) return 'normals size mismatch';
  if (m.colors.length !== vc * 3) return 'colors size mismatch';
  if (m.emissive.length !== vc * 3) return 'emissive size mismatch';
  if (m.indices.length % 3 !== 0) return 'indices not multiple of 3';
  for (const i of m.indices) if (i < 0 || i >= vc || !Number.isInteger(i)) return `index ${i} out of range`;
  for (const p of m.positions) if (!Number.isFinite(p)) return 'non-finite position';
  for (const n of m.normals) if (!Number.isFinite(n)) return 'non-finite normal';
  return null;
}
